// [PURPOSE]: Stripe checkout helpers for premium purchase and token packs
// [WHY]: Centralized purchase logic shared between PremiumGate, AccountPanel, etc.
// [INTEGRATION]: Calls /api/purchase-app and /api/purchase-tokens serverless routes

// [FIX]: API base URL — relative for web, full URL for Capacitor native
const API_BASE = window.Capacitor?.isNativePlatform()
  ? 'https://tabletopinventory.vercel.app/api'
  : '/api';

/**
 * [PURPOSE]: Initiate Stripe checkout for $3.99 premium purchase
 * [FLOW]: POST /api/purchase-app → get checkout URL → redirect user
 * [S9 2026-04-16]: Optional promoCode forwarded to the server for atomic redemption.
 *
 * @param {Object} currentUser - Firebase auth user
 * @param {string} [promoCode] - Optional promo code (e.g. "LAUNCH25"). Validated server-side.
 * @throws {Error} When promo validation fails, thrown error has a `reason` property
 *   (one of "invalid" | "inactive" | "expired" | "exhausted" | "already_used" | "race")
 *   plus `code: "PROMO_REJECTED"` so the UI can show a specific message instead of a generic failure.
 */
export const initiatePremiumPurchase = async (currentUser, promoCode) => {
  if (!currentUser) {
    throw new Error('Must be signed in to purchase');
  }

  const idToken = await currentUser.getIdToken();
  const body = {
    firebaseUid: currentUser.uid,
    email: currentUser.email,
  };
  if (promoCode) body.promoCode = promoCode;

  const response = await fetch(`${API_BASE}/purchase-app`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: 'Purchase failed' }));
    // [S9]: Preserve server's rejection reason so PremiumGate can surface a targeted message
    const err = new Error(payload.error || 'Failed to create checkout session');
    if (payload.reason) {
      err.code = 'PROMO_REJECTED';
      err.reason = payload.reason;
    }
    throw err;
  }

  const { url } = await response.json();
  window.location.href = url;
};

/**
 * [PURPOSE]: Server-side validation of a promo code for price-preview display
 * [FLOW]: POST /api/validate-promo → 200 with { valid, discount_type, discount_value, ... }
 * [S9 2026-04-16]: Read-only. Does NOT claim a redemption slot — that happens at purchase time.
 *
 * @param {Object} currentUser - Firebase auth user
 * @param {string} code - Promo code to validate (normalized server-side)
 * @returns {Promise<{valid: boolean, reason?: string, discount_type?: string, discount_value?: number, code?: string}>}
 */
export const validatePromoCode = async (currentUser, code) => {
  if (!currentUser) throw new Error('Must be signed in to validate promo codes');
  if (!code || typeof code !== 'string') return { valid: false, reason: 'invalid' };

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${API_BASE}/validate-promo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    return { valid: false, reason: 'server_error' };
  }

  return response.json();
};

/**
 * [PURPOSE]: Client-side preview of the final price after applying a promo
 * [WHY]: Stripe is the source of truth for the charged amount; this is display-only.
 *        Kept in sync with purchase-app.js premium unit_amount (399 cents).
 *
 * @param {number} baseCents - Pre-discount price in cents (premium = 399)
 * @param {Object} promo - Validated promo object from validatePromoCode
 * @returns {number} Final price in cents (>= 0)
 */
export const applyPromoPreview = (baseCents, promo) => {
  if (!promo || !promo.valid) return baseCents;
  if (promo.discount_type === 'percent') {
    return Math.max(0, Math.round(baseCents * (1 - (promo.discount_value || 0) / 100)));
  }
  if (promo.discount_type === 'amount') {
    return Math.max(0, baseCents - (promo.discount_value || 0));
  }
  return baseCents;
};

/**
 * [PURPOSE]: Initiate Stripe checkout for AI token pack purchase
 * [FLOW]: POST /api/purchase-tokens → get checkout URL → redirect user
 * @param {Object} currentUser - Firebase auth user
 * @param {string} packId - Token pack identifier ('standard', 'bulk', or 'ultimate')
 */
export const initiateTokenPurchase = async (currentUser, packId) => {
  if (!currentUser) {
    throw new Error('Must be signed in to purchase');
  }

  const idToken = await currentUser.getIdToken();
  const response = await fetch(`${API_BASE}/purchase-tokens`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      firebaseUid: currentUser.uid,
      email: currentUser.email,
      packId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Purchase failed' }));
    throw new Error(error.error || 'Failed to create checkout session');
  }

  const { url } = await response.json();
  window.location.href = url;
};
